import { useEffect, useState } from 'react'

// Presentation-only cache-age threshold, not a billing rule, reporting cadence, or service-health SLA.
const STALE_USAGE_MS = 2 * 60 * 60 * 1000

const record = (value: unknown): Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {}
)

function missingUsageMessage(summaries?: readonly unknown[]): string | null {
  if (!summaries) return null
  const rows = summaries.map(value => ({ state: record(value).state, billing: record(record(value).billing) }))
  const trialOnly = rows.length > 0 && rows.every(({ state, billing }) => state === 'trial'
    && billing.current_spend_minor === 0 && billing.effective_floor_minor === 0 && billing.effective_cap_minor === 0)
  if (trialOnly) return null
  if (rows.some(({ billing }) => billing.spend_basis === 'contract_bound' || billing.completeness === 'open_period_contract_bound')) {
    return 'Usage history incomplete · estimate uses package limits.'
  }
  return 'Usage observation time unavailable.'
}

const monotonicNow = () => {
  if (typeof performance === 'undefined' || typeof performance.now !== 'function') return null
  const value = performance.now()
  return Number.isFinite(value) ? value : null
}

export default function BillingUsageFreshness({ usageAsOf, asOf, period, packageSummaries, active = true, className = '' }: {
  usageAsOf?: string | null
  asOf?: string | null
  period?: { start?: string | null; end?: string | null } | null
  packageSummaries?: readonly unknown[]
  active?: boolean
  className?: string
}) {
  const observedAt = usageAsOf ? Date.parse(usageAsOf) : Number.NaN
  const evaluatedAt = asOf ? Date.parse(asOf) : Number.NaN
  const periodStart = period?.start ? Date.parse(period.start) : Number.NaN
  const periodEnd = period?.end ? Date.parse(period.end) : Number.NaN
  const validObservation = Number.isFinite(observedAt)
  const validEvaluation = Number.isFinite(evaluatedAt)
  const validPeriod = Number.isFinite(periodStart) && Number.isFinite(periodEnd) && periodStart < periodEnd
  const clockKey = validObservation && validEvaluation && active ? `${observedAt}:${evaluatedAt}` : null
  const [clock, setClock] = useState<{ key: string | null; elapsedMs: number }>({ key: null, elapsedMs: 0 })

  useEffect(() => {
    if (!clockKey) return
    const startedAt = monotonicNow()
    if (startedAt == null) return
    setClock({ key: clockKey, elapsedMs: 0 })
    const timer = window.setInterval(() => {
      const current = monotonicNow()
      if (current == null) return
      setClock(previous => previous.key === clockKey
        ? { key: clockKey, elapsedMs: Math.max(0, current - startedAt) }
        : previous)
    }, 60_000)
    return () => window.clearInterval(timer)
  }, [clockKey])

  if (!validObservation) {
    const message = missingUsageMessage(packageSummaries)
    return message ? <p className={`text-xs text-gray-500 dark:text-gray-400 ${className}`}>{message}</p> : null
  }
  const elapsedMs = clock.key === clockKey ? clock.elapsedMs : 0
  const evaluationNow = evaluatedAt + elapsedMs
  const stale = active && validEvaluation && validPeriod && observedAt >= periodStart && evaluationNow < periodEnd
    && observedAt <= evaluationNow && evaluationNow - observedAt > STALE_USAGE_MS

  return <div className={`text-xs text-gray-500 dark:text-gray-400 ${className}`}>
    <p>Usage through {new Date(observedAt).toLocaleString()}</p>
    {stale && <p role="status" className="mt-1 text-amber-700 dark:text-amber-300">Usage is over 2 hours old. This estimate may not include recent usage.</p>}
  </div>
}
